import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import ActionPlanApi from "../../../services/ActionPlanApi";

import FilterPanel from "../../../components/FilterPanel/FilterPanel";
import DataTable from "../../../components/DataTable/DataTable";
import Input from "../../../components/Input/Input";
import { Button } from "../../../components/Button/Button";
import { IconButton } from "../../../components/IconButton/IconButton";
import { Column } from "primereact/column";
import type { PaginatorPageChangeEvent } from "../../../components/Paginator/Paginator";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { formatDate } from "../../../utilities/date";

interface ActionPlan {
  id: string;
  name: string;
  description: string;
  type: string;
  status: number;
  operationName: string;
  forecastDate: string | null;
  completionDate: string | null;
  responsibleSector: string;
  responsiblePerson: string;
  errorProof: boolean;
  careLink: boolean;
}

const STORAGE_KEY = "TableFilterAdvanced_ActionPlan_filters";

const ListActionPlans: React.FC = () => {
  const [actionPlans, setActionPlans] = useState<ActionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { failureId, fmeaId } = useParams();
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { displayToast } = useToast();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string>("forecastDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // Estados dos filtros individuais
  const [filterName, setFilterName] = useState<string>(savedFilters.name?.value || "");
  const [filterStatus, setFilterStatus] = useState<number[]>(savedFilters.status?.value != null ? [savedFilters.status.value] : []);
  const [filterOperationName, setFilterOperationName] = useState<string>(savedFilters.operationName?.value || "");
  const [filterResponsiblePerson, setFilterResponsiblePerson] = useState<string>(savedFilters.responsiblePerson?.value || "");

  const statusOptions = [
    { id: "0", label: translateForm("planned"), value: 0 },
    { id: "1", label: translateForm("inProgress"), value: 1 },
    { id: "2", label: translateForm("completed"), value: 2 },
    { id: "3", label: translateForm("cancelled"), value: 3 },
  ];

  const fetchActionPlans = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc" | "desc" = sortOrder,
    pageSize: number = rows,
  ): Promise<void> => {
    if (!failureId) return;

    setIsLoading(true);
    try {
      const pageNumber = Math.floor(firstIndex / pageSize) + 1;
      const response = await ActionPlanApi.GetByFilterAsync(
        pageNumber,
        pageSize,
        field,
        direction,
        filters.name?.value || undefined,
        undefined, // description
        undefined, // type
        filters.status?.value != null ? filters.status.value : undefined,
        filters.operationName?.value || undefined,
        undefined, // forecastDate
        undefined, // sector
        filters.responsiblePerson?.value || undefined,
        failureId,
      );

      setActionPlans(response?.data?.actionPlans || []);
      setTotalRecords(response?.data?.total || 0);
    } catch (error) {
      displayToast("error", translateForm("errorFetchingActionPlans"), "", false, 4000);
      setActionPlans([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActionPlans(savedFilters, 0);
  }, []);

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      status: filterStatus.length > 0 ? { value: filterStatus[0] } : undefined,
      operationName: filterOperationName ? { value: filterOperationName } : undefined,
      responsiblePerson: filterResponsiblePerson ? { value: filterResponsiblePerson } : undefined,
    };

    // Remove undefined
    Object.keys(filters).forEach(key => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    fetchActionPlans(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterStatus([]);
    setFilterOperationName("");
    setFilterResponsiblePerson("");

    setPage(0);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    fetchActionPlans({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
    fetchActionPlans(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  const onSort = (e: any): void => {
    const dir = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(e.sortField);
    setSortOrder(dir);
    fetchActionPlans(savedFilters, page, e.sortField, dir);
  };

  async function deleteActionPlan(id: string): Promise<void> {
    try {
      await ActionPlanApi.DeleteActionPlan(id);
      setActionPlans((prev) => prev.filter((p) => p.id !== id));
      displayToast("success", translateForm("actionPlanSuccessfullyDeleted"), "", false, 3000);
    } catch {
      displayToast("error", translateForm("actionPlanNotDeleted"), "", false, 3000);
    }
  }

  const confirmDelete = (id: string): void => {
    confirmDialog({
      message: translateForm("actionPlanDeleteMessage") || translateForm("areYouSureDelete"),
      header: translateForm("confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: translateForm("Yes"),
      rejectLabel: translateForm("No"),
      acceptClassName: "p-button-accept",
      rejectClassName: "p-button-reject",
      accept: () => deleteActionPlan(id),
    });
  };

  const statusTranslations: Record<string | number, string> = {
    0: translateForm("planned"),
    1: translateForm("inProgress"),
    2: translateForm("completed"),
    3: translateForm("cancelled"),
  };

  const statusBodyTemplate = (rowData: ActionPlan): JSX.Element => {
    return <span>{statusTranslations[rowData.status] ?? rowData.status}</span>;
  };

  const descriptionBodyTemplate = (rowData: ActionPlan): JSX.Element => {
    const truncatedText =
      rowData.description.length > 30
        ? `${rowData.description.substring(0, 30)}...`
        : rowData.description;
    return <span title={rowData.description}>{truncatedText}</span>;
  };

  const dateBodyTemplate = (rowData: ActionPlan, field: keyof ActionPlan): JSX.Element | null => {
    const iso = rowData[field];
    if (!iso) return null;
    return <span>{formatDate(String(iso))}</span>;
  };

  const actionBodyTemplate = (rowData: ActionPlan): JSX.Element => {
    return (
      <div className="flex gap-2">
        <IconButton
          icon="edit"
          variant="text"
          severity="secondary"
          onClick={() =>
            navigate(
              `/fmeas/${fmeaId}/failures/${failureId}/action-plans/${rowData.id}/edit`,
            )
          }
          aria-label={translateGeneral("manage")}
        />
        <IconButton
          icon="delete"
          variant="text"
          severity="secondary"
          onClick={() => confirmDelete(rowData.id)}
          aria-label={translateGeneral("delete")}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-column gap-4">
      <ConfirmDialog />

      <FilterPanel
        title={translateForm("filter") || "Filtros"}
        onApply={handleFilter}
        onClear={handleClearFilters}
      >
        <Input
          label={translateForm("title")}
          type="text"
          value={filterName}
          onChange={setFilterName}
        />
        <Input
          label={translateForm("status")}
          type="multiselect"
          value={filterStatus}
          options={statusOptions}
          optionLabel="label"
          onChange={setFilterStatus}
        />
        <Input
          label={translateForm("responsiblePerson")}
          type="text"
          value={filterResponsiblePerson}
          onChange={setFilterResponsiblePerson}
        />
        <Input
          label="OP"
          type="text"
          value={filterOperationName}
          onChange={setFilterOperationName}
        />
      </FilterPanel>

      <div className="flex justify-content-end">
        <Button
          label={translateForm("register") || "Novo Plano de Ação"}
          icon="add"
          onClick={() => navigate(`/fmeas/${fmeaId}/failures/${failureId}/action-plans/create`)}
        />
      </div>

      <DataTable
        value={actionPlans}
        loading={isLoading}
        paginated
        first={page}
        rows={rows}
        totalRecords={totalRecords}
        onPageChange={handlePage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
      >
        <Column field="operationName" header="OP" sortable style={{ width: "8rem" }} />
        <Column field="name" header={translateForm("title")} sortable />
        <Column
          field="description"
          header={translateForm("description")}
          body={descriptionBodyTemplate}
          sortable
        />
        <Column field="type" header={translateForm("type")} sortable />
        <Column
          field="status"
          header={translateForm("status")}
          body={statusBodyTemplate}
          sortable
        />
        <Column
          field="forecastDate"
          header={translateForm("forecastDate")}
          body={(row: ActionPlan) => dateBodyTemplate(row, "forecastDate")}
          sortable
        />
        <Column field="responsibleSector" header={translateForm("responsibleSector")} sortable />
        <Column field="responsiblePerson" header={translateForm("responsiblePerson")} sortable />
        <Column header={translateForm("actions")} body={actionBodyTemplate} style={{ width: "8rem" }} />
      </DataTable>
    </div>
  );
};

export default ListActionPlans;
